import { AlertTriangle, Banknote, CalendarDays, FilePlus2, FileSpreadsheet, History, KeyRound, ListChecks, MapPinned, ScrollText, Settings2, Truck, WalletCards } from "lucide-react";
import AddInvoice from "./AddInvoice";
import BillHistory from "./BillHistory";
import BillsForCollection from "./BillsForCollection";
import ChequeReport from "./ChequeReport";
import CollectionsDashboard from "./CollectionsDashboard";
import CustomerWatchlist from "./CustomerWatchlist";
import DayAssignment from "./DayAssignment";
import DeliveryInvoiceAssignment from "./DeliveryInvoiceAssignment";
import LocationSummary from "./LocationSummary";
import OutstandingInvoicesStatement from "./OutstandingInvoicesStatement";
import PaymentEntry from "./PaymentEntry";
import PendingOtp from "./PendingOtp";
import ReceivableSettings from "./ReceivableSettings";

/**
 * Receivables module registry. Each entry maps a legacy CashVitae page to its React screen:
 *
 *   payment.aspx                      -> receivablePayments       (PaymentEntry)
 *   addInvoice.aspx                   -> receivableAddInvoice     (AddInvoice)
 *   OutstandingInvoicesStatement.aspx -> receivableOutstanding    (OutstandingInvoicesStatement)
 *   BillsForCollection.aspx           -> receivableBills          (BillsForCollection)
 *   delivery_inv_assignment.aspx      -> receivableDelivery       (DeliveryInvoiceAssignment)
 *   day_assignment.aspx               -> receivableDayAssignment  (DayAssignment)
 *   pending_otp.aspx                  -> receivablePendingOtp     (PendingOtp)
 *   cheque_report.aspx                -> receivableCheques        (ChequeReport)
 *   location_summary.aspx             -> receivableLocations      (LocationSummary)
 *   pdc_details.aspx                  -> receivableCollections    (CollectionsDashboard)
 *   RecDashboard.aspx (limit cards)   -> receivableWatchlist      (CustomerWatchlist)
 *   (TenantConfig flags)              -> receivableSettings       (ReceivableSettings)
 *
 * To wire it into the panel:
 *   - sidebar.js:   add `receivablesMenuGroup` to `menuGroups`
 *   - Dashboard.js: spread `receivablesFeatureMeta` into `featureMeta`, and in `renderContent()` return
 *                   `renderReceivablePage(activePage)` when `isReceivablePage(activePage)`
 */
export const receivablePages = [
  { featureKey: "receivablePayments", featureName: "Payment Receipts", icon: WalletCards, component: PaymentEntry },
  { featureKey: "receivableCollections", featureName: "Collections (PDC)", icon: ListChecks, component: CollectionsDashboard },
  { featureKey: "receivableCheques", featureName: "Cheque Report", icon: Banknote, component: ChequeReport },
  { featureKey: "receivableOutstanding", featureName: "Outstanding Statement", icon: ScrollText, component: OutstandingInvoicesStatement },
  { featureKey: "receivableBills", featureName: "Bills For Collection", icon: FileSpreadsheet, component: BillsForCollection },
  { featureKey: "receivableAddInvoice", featureName: "Add Invoice", icon: FilePlus2, component: AddInvoice },
  { featureKey: "receivableDelivery", featureName: "Delivery Assignment", icon: Truck, component: DeliveryInvoiceAssignment },
  { featureKey: "receivableDayAssignment", featureName: "Day Assignment", icon: CalendarDays, component: DayAssignment },
  { featureKey: "receivablePendingOtp", featureName: "Pending OTP", icon: KeyRound, component: PendingOtp },
  { featureKey: "receivableWatchlist", featureName: "Customer Watchlist", icon: AlertTriangle, component: CustomerWatchlist },
  { featureKey: "receivableLocations", featureName: "Location Summary", icon: MapPinned, component: LocationSummary },
  { featureKey: "receivableBillHistory", featureName: "Payment / Bill No History", icon: History, component: BillHistory },
  // shown under Admin in the sidebar (as "Settings"), not under Receivables
  { featureKey: "receivableSettings", featureName: "Settings", icon: Settings2, component: ReceivableSettings, group: "Admin" },
];

export const receivablesMenuGroup = {
  groupKey: "receivables",
  groupName: "Receivables",
  icon: WalletCards,
  items: receivablePages.filter((page) => !page.group).map(({ featureKey, featureName, icon }) => ({ featureKey, featureName, icon })),
};

export const receivablesFeatureMeta = Object.fromEntries(
  receivablePages.map(({ featureKey, featureName, group }) => [featureKey, { label: featureName, crumb: group || "Receivables" }])
);

export const isReceivablePage = (featureKey) => receivablePages.some((page) => page.featureKey === featureKey);

export const renderReceivablePage = (featureKey) => {
  const page = receivablePages.find((p) => p.featureKey === featureKey);
  if (!page) return null;
  const Component = page.component;
  return <Component />;
};
